import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Popover } from '@mui/material';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  ChevronDown,
  Code2,
  ExternalLink,
  Home,
  LayoutDashboard,
  Menu,
  Share2,
  Users,
  X,
} from 'lucide-react';
import logo from 'src/assets/sonio-logo-black.png';
import SonioSmallLogo from 'src/assets/sonio-mark.svg';
import StandaloneToggleButton from 'src/components/ToggleButton';
import { useAppSelector } from 'src/hooks/useStore';
import { useUserRoles } from 'src/hooks/useUserRoles';
import SidebarTopSection from 'src/layouts/NewSidebar/Components/organizations';
import {
  AbilityContext,
  canAccessDevHub,
  type Subjects,
} from 'src/lib/ability';
import { getStatusHealthUrl } from 'src/services/api.config';

import { SidebarFooter } from '../NewSidebar/Components/sideFooter';

type MenuChild = {
  label: string;
  link: string;
  external?: boolean;
  subject?: Subjects;
};

type MenuItem = {
  label: string;
  icon: typeof Home;
  link?: string;
  children?: MenuChild[];
};

const allMenuItems: MenuItem[] = [
  { label: 'Home', icon: Home, link: '/home' },
  {
    label: 'Sessions',
    icon: LayoutDashboard,
    children: [
      { label: 'Overview', link: '/transactions/sessions' },
      { label: 'Statistics', link: '/transactions/statistics' },
    ],
  },
  {
    label: 'Users',
    icon: Users,
    children: [
      { label: 'List', link: '/users' },
      { label: 'Roles', link: '/users/team-management' },
      { label: 'Permissions', link: '/permissions' },
    ],
  },
  { label: 'Organizations', icon: Building2, link: '/organizations' },
  {
    label: 'Flows',
    icon: Share2,
    children: [
      { label: 'Flow Management', link: '/flows/flow-list' },
      { label: 'ID Upload', link: '/id-upload' },
    ],
  },
  {
    label: 'Dev Hub',
    icon: Code2,
    children: [
      {
        label: 'API Keys',
        link: '/developers/api-keys',
        subject: 'DevHubApiKeys',
      },
      {
        label: 'Webhooks',
        link: '/developers/webhooks',
        subject: 'DevHubWebhooks',
      },
      {
        label: 'Session expiry',
        link: '/developers/sessions',
        subject: 'DevHubSessionExpiry',
      },
      {
        label: 'Documentation',
        link: '/api-docs',
        external: true,
        subject: 'DevHubDocumentation',
      },
      {
        label: 'Status',
        link: '',
        external: true,
        subject: 'DevHubStatus',
      },
    ],
  },
];

export default function ShadcnSidebar() {
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  const [collapsedState, setCollapsed] = useState(false);
  // Below 768px the sidebar becomes an off-canvas drawer opened from a top bar
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const collapsed = !isMobile && collapsedState;
  const [popoverAnchor, setPopoverAnchor] = useState<{
    element: HTMLElement | null;
    label: string;
  } | null>(null);
  const menuItemRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const ability = useContext(AbilityContext);
  const { roles: _roles } = useUserRoles();
  const { liveData } = useAppSelector(state => state.sliceTransaction);
  const statusHealthUrl = getStatusHealthUrl(liveData);

  // Filter menu items based on CASL abilities
  const menu = useMemo(() => {
    return allMenuItems
      .map(item => {
        if (item.label !== 'Dev Hub' || !item.children) return item;
        return {
          ...item,
          children: item.children.map(child =>
            child.label === 'Status'
              ? { ...child, link: statusHealthUrl }
              : child,
          ),
        };
      })
      .filter(item => {
        if (item.label === 'Home') {
          return ability.can('read', 'Home');
        }
        if (item.label === 'Sessions') {
          return ability.can('read', 'Sessions');
        }
        if (item.label === 'Users') {
          return ability.can('read', 'Users');
        }
        if (item.label === 'Organizations') {
          return ability.can('read', 'Organizations');
        }
        if (item.label === 'Flows') {
          // Gate purely on CASL abilities — the ability object already encodes
          // both role-based and external_permissions rules, so we don't need
          // to check the role name here (avoids races with async ability load).
          return (
            ability.can('read', 'Flows') ||
            ability.can('manage', 'Flows') ||
            ability.can('read', 'IdUpload') ||
            ability.can('create', 'IdUpload')
          );
        }
        if (item.label === 'Dev Hub') {
          return canAccessDevHub(ability);
        }
        return false;
      })
      .map(item => {
        // Flows: show only children the user has permission to access
        if (item.label === 'Flows' && item.children) {
          const canReadFlows = ability.can('read', 'Flows');
          const canIdUpload =
            ability.can('read', 'IdUpload') ||
            ability.can('create', 'IdUpload');
          const filteredChildren = item.children.filter(child => {
            if (child.label === 'Flow Management') {
              return canReadFlows;
            }
            if (child.label === 'ID Upload') {
              return canIdUpload;
            }
            return false;
          });
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        if (item.label === 'Dev Hub' && item.children) {
          const canAllDevHub =
            ability.can('manage', 'all') || ability.can('read', 'Developers');
          const filteredChildren = item.children.filter(child => {
            if (canAllDevHub) return true;
            return child.subject
              ? ability.can('read', child.subject) ||
                  ability.can('update', child.subject)
              : false;
          });
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter((item): item is NonNullable<typeof item> => item != null);
  }, [ability, statusHealthUrl]);
  useEffect(() => {
    const handleResize = () => {
      const shouldCollapse = window.innerWidth < 1280; // collapse on smaller screens
      setCollapsed(shouldCollapse);
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close popover when sidebar state changes
  useEffect(() => {
    if (!collapsed && popoverAnchor) {
      setPopoverAnchor(null);
    }
  }, [collapsed, popoverAnchor]);
  const navigate = useNavigate();
  const location = useLocation();

  // Close the mobile drawer whenever the route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock page scroll while the mobile drawer is open
  useEffect(() => {
    if (!isMobile || !mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobile, mobileOpen]);

  const toggleMenu = (label: string) => {
    setOpenMenus(prev => {
      // Keep all existing menus open, just toggle the clicked one
      return { ...prev, [label]: !prev[label] };
    });
  };

  const toggleSidebar = () => {
    setCollapsed(!collapsedState);
  };

  const isActiveRoute = (link: string) => {
    return (
      location.pathname === link ||
      (link === '/home' && location.pathname === '/')
    );
  };

  const isParentActive = (item: (typeof menu)[0]) => {
    if (item.link) {
      return isActiveRoute(item.link);
    }
    if (item.children) {
      return item.children.some(
        child => !child.external && isActiveRoute(child.link),
      );
    }
    return false;
  };

  const openMenuChild = (sub: MenuChild) => {
    if (sub.external) {
      const href = sub.link.startsWith('http')
        ? sub.link
        : `${window.location.origin}${sub.link}`;
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(sub.link);
  };

  const subItemClass = (active: boolean) =>
    `w-full flex items-center justify-between gap-2 text-left py-[7px] px-2.5 text-sm rounded-lg transition-colors ${
      active
        ? 'bg-[#00387D] text-white font-semibold'
        : 'text-[#6B6B6B] hover:text-[#00387D] hover:bg-[#F7F9FC]'
    }`;

  return (
    <div className="flex bg-[#F7F9FC] min-h-screen overflow-x-hidden">
      {/* Mobile top bar */}
      {isMobile && (
        <header className="fixed top-0 inset-x-0 h-14 bg-white border-b-[1.5px] border-[#E5E7EB] flex items-center gap-2.5 px-4 z-40">
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
            className="h-9 w-9 flex items-center justify-center rounded-md border-[1.5px] border-[#E5E7EB] text-[#0C2340]"
          >
            <Menu strokeWidth={1.75} className="h-[18px] w-[18px]" />
          </button>
          <img
            src={logo}
            alt="SONIO"
            className="h-[20px] cursor-pointer"
            onClick={() => navigate('/home')}
          />
          <div className="ml-auto w-[132px] rounded-lg bg-[#F7F9FC] border-[1.5px] border-[#E5E7EB] p-[2px]">
            <StandaloneToggleButton collapsed={false} />
          </div>
        </header>
      )}

      {/* Mobile drawer backdrop */}
      {isMobile && mobileOpen && (
        <div
          aria-hidden
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-[#0C2340]/35 z-40"
        />
      )}

      {/* Collapse toggle */}
      {!isMobile && (
        <button
          type="button"
          onClick={toggleSidebar}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="fixed top-[30px] w-[26px] h-[26px] bg-white rounded-full flex items-center justify-center border-[1.5px] border-[#E5E7EB] text-[#5B6B80] hover:text-[#00387D] hover:border-[#00387D] shadow-[0_2px_6px_rgba(12,35,64,0.08)] transition-all duration-300 z-[60]"
          style={{ left: collapsed ? '67px' : '259px' }}
        >
          {collapsed ? (
            <ArrowRight className="h-3.5 w-3.5" />
          ) : (
            <ArrowLeft className="h-3.5 w-3.5" />
          )}
        </button>
      )}

      <aside
        className={`${
          isMobile
            ? `w-[min(300px,86vw)] z-50 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`
            : `${collapsed ? 'w-20' : 'w-[272px]'} z-10`
        } bg-white border-r border-[#E4EBF4] px-4 pt-5 pb-4 transition-all duration-300 flex flex-col gap-3.5 fixed left-0 top-0 h-screen h-[100dvh] overflow-y-auto overflow-x-hidden`}
      >
        {/* Logo */}
        <div
          className={`flex items-center h-10 ${
            collapsed ? 'justify-center' : 'justify-start px-1.5'
          }`}
        >
          {isMobile && (
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              className="ml-auto order-2 h-8 w-8 flex items-center justify-center rounded-md text-[#5B6B80] hover:text-[#0C2340]"
            >
              <X strokeWidth={1.75} className="h-5 w-5" />
            </button>
          )}
          {collapsed ? (
            <img
              src={SonioSmallLogo}
              alt="SONIO"
              className="h-7 cursor-pointer"
              onClick={() => navigate('/home')}
            />
          ) : (
            <img
              src={logo}
              alt="SONIO"
              className="h-[26px] cursor-pointer"
              onClick={() => navigate('/home')}
            />
          )}
        </div>

        {/* Live / Test toggle (in the top bar on mobile) */}
        <div className="max-md:hidden rounded-xl bg-[#F7F9FC] border-[1.5px] border-[#E5E7EB] p-[3px]">
          <StandaloneToggleButton collapsed={collapsed} />
        </div>

        {/* Organization selector */}
        <div>
          <SidebarTopSection collapsed={collapsed} />
        </div>

        {!collapsed && (
          <span className="px-2.5 pt-1 text-[11px] font-bold tracking-[0.14em] text-[#5B6B80] uppercase">
            Menu
          </span>
        )}

        {/* Menu items */}
        <nav className="flex flex-col gap-0.5 -mt-1.5">
          {menu.map(item => {
            const isOpen = openMenus[item.label] || false;
            const isActive = isParentActive(item);
            const hasChildren = !!item.children;
            // Direct link: solid fill when active
            const shouldHighlightParent = isActive && !hasChildren;
            // Group with an active child: soft tint
            const shouldTintParent = isActive && hasChildren;
            return (
              <div key={item.label}>
                <button
                  type="button"
                  title={collapsed ? item.label : undefined}
                  ref={el => {
                    menuItemRefs.current[item.label] = el;
                  }}
                  className={`flex w-full items-center gap-3 rounded-[10px] text-left text-[14.5px] transition-colors ${
                    collapsed ? 'justify-center py-1.5' : 'px-2.5 py-2'
                  } ${
                    shouldHighlightParent
                      ? 'bg-[#00387D] text-white font-semibold'
                      : shouldTintParent
                        ? 'bg-[#EAF0F9] text-[#0C2340] font-semibold'
                        : 'text-[#252525] font-medium hover:bg-[#F7F9FC]'
                  }`}
                  onClick={() => {
                    if (item.link) {
                      navigate(item.link);
                      // Close all menus when navigating to a direct link
                      setOpenMenus({});
                      setPopoverAnchor(null);
                    } else if (item.children) {
                      if (collapsed) {
                        // When collapsed, show popover
                        setPopoverAnchor({
                          element: menuItemRefs.current[item.label],
                          label: item.label,
                        });
                      } else {
                        // When expanded, toggle dropdown
                        toggleMenu(item.label);
                      }
                    }
                  }}
                >
                  <span className="flex items-center justify-center h-8 w-6 flex-shrink-0">
                    <item.icon
                      strokeWidth={1.75}
                      className={`h-5 w-5 ${
                        shouldHighlightParent
                          ? 'text-white'
                          : shouldTintParent
                            ? 'text-[#00387D]'
                            : 'text-[#5B6B80]'
                      }`}
                    />
                  </span>
                  {!collapsed && <span className="flex-1">{item.label}</span>}
                  {!collapsed && item.children ? (
                    <ChevronDown
                      className={`h-4 w-4 text-[#5B6B80] transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  ) : null}
                </button>

                {!collapsed && item.children && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="flex flex-col gap-0.5 ml-[26px] pl-3.5 mt-1 mb-1.5 border-l-2 border-[#E5E7EB]">
                      {item.children.map(sub => {
                        const isSubActive =
                          !sub.external && isActiveRoute(sub.link);
                        return (
                          <button
                            type="button"
                            key={sub.label}
                            className={subItemClass(isSubActive)}
                            onClick={() => {
                              openMenuChild(sub);
                              if (!sub.external) {
                                setOpenMenus(prev => {
                                  const newState = Object.keys(prev).reduce(
                                    (acc, key) => {
                                      acc[key] = false;
                                      return acc;
                                    },
                                    {} as { [key: string]: boolean },
                                  );
                                  // Keep only the current parent menu open
                                  newState[item.label] = true;
                                  return newState;
                                });
                              }
                              setPopoverAnchor(null);
                            }}
                          >
                            <span>{sub.label}</span>
                            {sub.external && (
                              <ExternalLink
                                className={`h-3.5 w-3.5 shrink-0 ${
                                  isSubActive ? 'text-white' : 'text-[#5B6B80]'
                                }`}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Spacer to push footer to bottom */}
        <div className="flex-1" />

        {/* User footer */}
        <div
          className={`pt-3.5 ${!collapsed ? 'border-t border-[#E4EBF4]' : ''}`}
        >
          <SidebarFooter collapsed={collapsed} />
        </div>
      </aside>

      {/* Popover for collapsed menu items with children */}
      {popoverAnchor && (
        <Popover
          open={!!popoverAnchor}
          anchorEl={popoverAnchor.element}
          onClose={() => setPopoverAnchor(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableScrollLock
          PaperProps={{
            sx: {
              borderRadius: '14px',
              boxShadow: '0 12px 32px rgba(12, 35, 64, 0.14)',
              border: '1px solid #E5E7EB',
              overflow: 'hidden',
              mt: 0,
              ml: 1,
            },
          }}
        >
          <div className="p-2 min-w-[200px]">
            <div className="px-2.5 pt-1 pb-1.5 text-[11px] font-bold tracking-[0.14em] text-[#5B6B80] uppercase">
              {popoverAnchor.label}
            </div>
            {menu
              .find(item => item.label === popoverAnchor.label)
              ?.children?.map(sub => {
                const isSubActive = !sub.external && isActiveRoute(sub.link);
                return (
                  <button
                    type="button"
                    key={sub.label}
                    className={subItemClass(isSubActive)}
                    onClick={() => {
                      openMenuChild(sub);
                      setPopoverAnchor(null);
                    }}
                  >
                    <span>{sub.label}</span>
                    {sub.external && (
                      <ExternalLink
                        className={`h-3.5 w-3.5 shrink-0 ${
                          isSubActive ? 'text-white' : 'text-[#5B6B80]'
                        }`}
                      />
                    )}
                  </button>
                );
              })}
          </div>
        </Popover>
      )}

      {/* Main content */}
      <main
        className={`flex-1 overflow-x-hidden overflow-y-auto bg-[#F7F9FC] w-full max-w-full transition-all duration-300 ${
          isMobile ? 'ml-0 pt-14' : collapsed ? 'ml-20' : 'ml-[272px]'
        }`}
      >
        <div className="w-full max-w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
